/**
 * Dates and counts, as the Publisher prints them.
 *
 * Pure functions with no React and no store access, so they are unit-testable — the
 * same rule lib/formatting.ts follows.
 *
 * WHY A FILE FOR THIS. §26 forbids fabricated timestamps, and the way a fabricated
 * timestamp actually gets shipped is not somebody inventing one on purpose: it is a
 * formatter given an empty string or an unparseable value and quietly rendering
 * "Invalid Date", "Jan 1, 1970", or today's date. Each of those is a date the provider
 * never reported. So every function here returns a null-ish marker for input it cannot
 * parse, and the callers print that marker rather than a number.
 */
import { GM_LANGUAGES } from '../data/music';

/** What is shown where a date should be but is not. Never a date. */
export const NO_DATE = '—';

/**
 * A date the user chose or a provider reported, e.g. "12 Aug 2026".
 *
 * Locale-formatted through Intl, as useEntitlements and the products catalogue already
 * do, so the reader sees their own convention rather than a US one.
 *
 * Returns NO_DATE for an empty or unparseable value. `Number.isNaN(getTime())` is the
 * check that matters: `new Date('')` and `new Date('soon')` both produce an Invalid
 * Date, and formatting one yields the literal string "Invalid Date" on screen.
 */
export function formatReleaseDate(iso: string | null | undefined): string {
  if (!iso) return NO_DATE;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NO_DATE;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * A timestamp with the time of day, for "last updated" and for the event history.
 *
 * The minute is included because a release's events can be seconds apart — a draft
 * created, edited and validated in one sitting — and a timeline where four entries all
 * read the same date says nothing about their order.
 */
export function formatTimestamp(iso: string | null | undefined): string {
  if (!iso) return NO_DATE;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return NO_DATE;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * The languages a release can declare, as code/label pairs.
 *
 * ============================================================================
 * THE STORED VALUE IS THE CODE, NOT THE WORD ON SCREEN.
 *
 * `audio_language` is required on `POST /tracks`, and LabelGrid's own examples use
 * language codes. Storing "English" on the release would put the string "English" in
 * that field, which is a value the API did not ask for — so `PublisherRelease.language`
 * holds the code and this is where the two are joined.
 *
 * `zxx` for an instrumental is not invented either: it is the ISO 639-2 code for "no
 * linguistic content", which is the standard way to say a recording has no lyrics.
 *
 * The LABELS come from data/music.GM_LANGUAGES — the same eleven the composer and the
 * Genre Mixer offer — so the Publisher cannot end up offering a different set of
 * languages from the rest of the app. The codes are listed explicitly because a code
 * cannot be derived from a label.
 * ============================================================================
 */
export const PUBLISHER_LANGUAGE_CODES: Readonly<Record<string, string>> = {
  English: 'en',
  Spanish: 'es',
  French: 'fr',
  Portuguese: 'pt',
  German: 'de',
  Italian: 'it',
  Japanese: 'ja',
  Korean: 'ko',
  Hindi: 'hi',
  Arabic: 'ar',
  Instrumental: 'zxx',
};

/**
 * The language options for a `<Select>`, in the app's own order.
 *
 * Built from GM_LANGUAGES rather than from the record above, so a language added to the
 * app's list but not given a code here is DROPPED rather than sent as an empty string.
 * Silently offering a language the API cannot be told about is the failure mode this
 * guards against.
 */
export const PUBLISHER_LANGUAGE_OPTIONS: readonly { value: string; label: string }[] =
  GM_LANGUAGES.filter((label) => Boolean(PUBLISHER_LANGUAGE_CODES[label])).map((label) => ({
    value: PUBLISHER_LANGUAGE_CODES[label],
    label,
  }));

/**
 * The word for a stored language code, e.g. `en` → "English".
 *
 * Falls back to the code itself rather than to "Unknown": a code that came from a
 * provider response is a real value, and printing it verbatim is honest where inventing
 * a name for it would not be.
 */
export function languageLabel(code: string | null | undefined): string {
  if (!code) return NO_DATE;
  const match = PUBLISHER_LANGUAGE_OPTIONS.find((option) => option.value === code);
  return match ? match.label : code;
}

/**
 * "1 outlet" / "3 outlets" / "No outlets".
 *
 * §6 asks the release list to show an outlet count. It is the user's SELECTION, not a
 * delivery claim — nothing has been delivered anywhere — so the wording says "selected"
 * at the call site and this only handles the number and the plural.
 */
export function outletCountLabel(count: number): string {
  if (count <= 0) return 'No outlets';
  return count === 1 ? '1 outlet' : `${count} outlets`;
}

/**
 * "1 track" / "8 tracks".
 *
 * Same shape, and worth having beside the one above so the two pluralisations cannot
 * drift apart across the four screens that print them.
 */
export function trackCountLabel(count: number): string {
  if (count <= 0) return 'No tracks';
  return count === 1 ? '1 track' : `${count} tracks`;
}
